import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { JsonLdScript } from 'components/atoms';

import { metaPropTypes } from 'config/propTypes';
import statePageContent from 'config/statePageContent';
import stateSelector from 'store/selectors/state';
import { Page } from 'components/core/Page';
import { StateHero } from 'components/organisms/StateHero';
import CityCardList from 'components/organisms/CityCardList/CityCardList';
import BreadcrumbsBar from 'components/organisms/BreadcrumbsBar/BreadcrumbsBar';
import usePageViewTracking from 'hooks/usePageViewTracking';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import StatePageSkeleton from './StatePageSkeleton';

import './StatePage.scss';

export function StatePage({
	trackingPixel: trackingPixelProp,
	meta: metaProp,
	location: locationProp,
	stateUrl: stateUrlProp,
	drilldownLinks: drilldownLinksProp,
	isLoading: isLoadingProp,
	statusCode: statusCodeProp,
}) {
	const selectorResult = useSelector(stateSelector);

	const {
		trackingPixel: storeTrackingPixel,
		meta: storeMeta,
		location: storeLocation,
		stateUrl: storeStateUrl,
		drilldownLinks: storeDrilldownLinks,
		isLoading: storeIsLoading,
	} = selectorResult || {};

	const trackingPixel = trackingPixelProp ?? storeTrackingPixel ?? null;
	const meta = metaProp ?? storeMeta ?? {};
	const location = storeLocation ?? locationProp ?? null;

	const stateUrl = stateUrlProp ?? storeStateUrl ?? '';
	const drilldownLinks = drilldownLinksProp ?? storeDrilldownLinks;
	const isLoading = isLoadingProp ?? storeIsLoading ?? false;
	const statusCode = statusCodeProp ?? selectorResult?.statusCode;

	usePageViewTracking(PAGE_TYPES.STATE_LANDING);

	if (statusCode > 200) {
		return <ErrorPage statusCode={statusCode} />;
	}

	if (isLoading || !location) {
		return <StatePageSkeleton />;
	}

	const content = location.state ? statePageContent[location.state] : null;

	const websiteSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'SelfStorage',
		url: 'https://www.selfstorage.com/',
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: `https://www.selfstorage.com/search?location=${location.fullState}%2C%20USA`,
				inLanguage: 'en-US',
				actionPlatform: [
					'https://schema.org/DesktopWebPlatform',
					'https://schema.org/MobileWebPlatform',
				],
			},
			'query-input': {
				'@type': 'PropertyValueSpecification',
				valueRequired: 'https://schema.org/True',
				valueName: 'search_term_string',
				valuePattern: location.fullState,
			},
		},
	};

	const webPageSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		name: `${location.fullState} Self Storage | Find Self Storage Options by State`,
		url: meta?.canonical || '',
		description: `Explore the most affordable self-storage options in ${location.fullState}. Quickly locate facilities in your city and reserve the perfect unit online in minutes.`,
		mainEntity: {
			'@type': 'Place',
			name: location.fullState,
		},
	};

	return (
		<Page
			className="state-landing-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.STATE_LANDING}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
		>
			<JsonLdScript schema={websiteSchema} />
			<JsonLdScript schema={webPageSchema} />
			<BreadcrumbsBar
				location={location}
				stateUrl={stateUrl}
			/>
			<StateHero
				location={location}
				stateUrl={stateUrl}
				stateNickname={content?.stateNickname}
			/>

			{!statePageContent[location.state]?.isDC &&
				drilldownLinks?.length > 0 && (
					<CityCardList drilldownLinks={drilldownLinks} />
				)}
		</Page>
	);
}

StatePage.propTypes = {
	drilldownLinks: PropTypes.arrayOf(
		PropTypes.shape({
			city: PropTypes.string,
			state: PropTypes.string,
			content: PropTypes.shape({
				unitTypes: PropTypes.array,
				vehicleStorage: PropTypes.array,
				amenities: PropTypes.array,
			}),
		}),
	),
	location: PropTypes.shape({
		state: PropTypes.string,
		fullState: PropTypes.string,
	}),
	stateUrl: PropTypes.string,
	trackingPixel: PropTypes.string,
	meta: PropTypes.shape(metaPropTypes),
	isLoading: PropTypes.bool,
	statusCode: PropTypes.number,
};

export default StatePage;
